import { tokenStorage } from "../utils/auth/tokenStorage";
import config from "../config/environment";
import {
  withExponentialBackoff,
  isRetryableError,
} from "../utils/exponentialBackoff";

interface TokenRefreshServiceConfig {
  refreshThresholdMinutes?: number;
  checkIntervalSeconds?: number;
}

class TokenRefreshService {
  private refreshThresholdMinutes: number;
  private checkIntervalSeconds: number;
  private intervalId: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;

  constructor(config: TokenRefreshServiceConfig = {}) {
    this.refreshThresholdMinutes = config.refreshThresholdMinutes || 5;
    this.checkIntervalSeconds = config.checkIntervalSeconds || 30;
  }

  private async refreshToken(): Promise<void> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      try {
        const refreshToken = tokenStorage.getRefreshToken();
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Use exponential backoff for token refresh
        const result = await withExponentialBackoff(
          async () => {
            const response = await fetch(
              `${config.api.url}/trpc/auth.refreshToken`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Refresh-Token": refreshToken,
                },
                body: JSON.stringify({
                  json: { refreshToken },
                }),
              },
            );

            if (!response.ok) {
              const error = new Error("Failed to refresh token");
              (error as any).response = { status: response.status };
              throw error;
            }

            return await response.json();
          },
          {
            maxRetries: 3,
            initialDelay: 1000,
            maxDelay: 10000,
          },
          (error) => {
            // Don't retry on 401 (invalid refresh token)
            if (error.response?.status === 401) {
              return false;
            }
            return isRetryableError(error);
          },
        );

        const refreshResult = result.result?.data?.json;

        if (!refreshResult?.accessToken && !refreshResult?.token) {
          throw new Error("Invalid refresh response");
        }

        // Handle both possible response formats
        const newAccessToken = refreshResult.accessToken || refreshResult.token;
        const newRefreshToken = refreshResult.refreshToken;

        tokenStorage.setAuthToken(newAccessToken);

        // Always update refresh token if provided (token rotation)
        if (newRefreshToken) {
          tokenStorage.setRefreshToken(newRefreshToken);
        }

        console.log("Token refreshed proactively");
      } catch (error) {
        console.error("Proactive token refresh failed after retries:", error);

        if (
          error instanceof Error &&
          (error.message.includes("refresh token") ||
            (error as any).response?.status === 401)
        ) {
          tokenStorage.clearTokens();
          window.dispatchEvent(new CustomEvent("auth:expired"));
        }

        throw error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async checkAndRefreshToken(): Promise<void> {
    try {
      const token = tokenStorage.getAuthToken();
      const refreshToken = tokenStorage.getRefreshToken();

      if (!token || !refreshToken) {
        return;
      }

      const tokenData = tokenStorage.getTokenData();
      if (!tokenData?.expiresAt) {
        return;
      }

      const expiresAt = new Date(tokenData.expiresAt);
      const now = new Date();
      const minutesUntilExpiry =
        (expiresAt.getTime() - now.getTime()) / (1000 * 60);

      if (
        minutesUntilExpiry <= this.refreshThresholdMinutes &&
        minutesUntilExpiry > 0
      ) {
        console.log(
          `Token expiring in ${minutesUntilExpiry.toFixed(1)} minutes, refreshing proactively...`,
        );
        await this.refreshToken();
      }
    } catch (error) {
      console.error("Error checking token expiration:", error);
    }
  }

  public start(): void {
    if (this.intervalId) {
      return;
    }

    this.checkAndRefreshToken();

    this.intervalId = setInterval(() => {
      this.checkAndRefreshToken();
    }, this.checkIntervalSeconds * 1000);

    console.log("Token refresh service started");
  }

  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log("Token refresh service stopped");
    }
  }

  public async forceRefresh(): Promise<void> {
    return this.refreshToken();
  }

  public isTokenExpiringSoon(): boolean {
    const tokenData = tokenStorage.getTokenData();
    if (!tokenData?.expiresAt) {
      return false;
    }

    const expiresAt = new Date(tokenData.expiresAt);
    const now = new Date();
    const minutesUntilExpiry =
      (expiresAt.getTime() - now.getTime()) / (1000 * 60);

    return (
      minutesUntilExpiry <= this.refreshThresholdMinutes &&
      minutesUntilExpiry > 0
    );
  }
}

export const tokenRefreshService = new TokenRefreshService({
  refreshThresholdMinutes: 5,
  checkIntervalSeconds: 30,
});

window.addEventListener("storage", (e) => {
  if (e.key === "auth_token" && !e.newValue) {
    tokenRefreshService.stop();
  }
});

export default tokenRefreshService;
